import moment from 'moment';
import { generateTimestampsInSeconds, toDuration, formatDateTime } from '@/util/date';
import { DISPLAY_DATETIME } from '@/util/apiDatetime';
import { kmhToKnot, metersToNm } from '@/util/units';
import { formatNum } from '@/util/number';
import { tempAverageC } from '@/util/temperature';

/**
 * Transform RPM type label for display purposes.
 * If there's only 1 RPM type and it's 'CENTER', display as 'MAIN' instead.
 * Data mapping remains unchanged (still uses 'CENTER' internally).
 *
 * @param {string} rpmType - The RPM type (PORT, STARBOARD, CENTER)
 * @param {string[]} allRpmTypes - Array of all available RPM types
 * @returns {string} - Display label for the RPM type
 */
export function getRpmTypeLabel(rpmType, allRpmTypes = []) {
  // If only one RPM type exists and it's CENTER, display as MAIN
  if (allRpmTypes.length === 1 && rpmType === 'CENTER') {
    return 'MAIN';
  }
  return rpmType;
}

/**
 * Kelompokin nomor AE (1..aeCount) ke channel flowmeter yang nyuplai dia,
 * murni dari nama group flowmeter (label device dikurangin _IN/_OUT).
 * Group bernama persis AEn (AE4, AE5, ...) = line punya sendiri buat AE itu.
 * Group AE lain (AE_INLINE, dst) = line bareng, nampung sisa AE yang belum diklaim.
 * Group non-AE (PORT, STARBOARD) BUKAN line AE. Dulu aturannya cuma "bukan AEn",
 * jadi flowmeter Main Engine Port kelaporan sebagai konsumsi AE terukur.
 *
 * ponytail: kalau ada >1 line "bareng" dalam satu asset, cuma yang pertama
 * ketemu yang kepake — upgrade heuristik ini kalau kasusnya beneran muncul.
 */
export function buildAeFuelChannels(aeCount, flowmeterGroups = []) {
  const dedicated = {};
  flowmeterGroups.forEach((group) => {
    const match = /^AE(\d+)$/i.exec(group.name);
    if (match) dedicated[Number(match[1])] = group.name;
  });
  const sharedGroup = flowmeterGroups.find(
    (group) => /^AE/i.test(group.name) && !/^AE\d+$/i.test(group.name),
  );

  const channels = [];
  const sharedAeNums = [];
  const unmeteredAeNums = [];
  for (let i = 1; i <= aeCount; i += 1) {
    if (dedicated[i]) {
      channels.push({ groupName: dedicated[i], aeNums: [i] });
    } else if (sharedGroup) {
      sharedAeNums.push(i);
    } else {
      unmeteredAeNums.push(i);
    }
  }
  if (sharedAeNums.length) {
    channels.unshift({ groupName: sharedGroup.name, aeNums: sharedAeNums });
  }
  // AE tanpa line flowmeter tetep dapet baris — konsumsinya cuma dari konstanta.
  // groupName null = "gak terukur", bukan "nol liter".
  if (unmeteredAeNums.length) {
    channels.push({ groupName: null, aeNums: unmeteredAeNums });
  }
  return channels;
}

export function getIntervalSeconds(interval) {
  switch (interval) {
    case 'minute':
      return 60;
    case 'hour':
      return 60 * 60;
    case 'day':
      return 60 * 60 * 24;
    case 'week':
      return 60 * 60 * 24 * 7;
    default:
      return 60;
  }
}

function roundKeyByInterval(key, interval) {
  // Round down to the interval boundary using startOf
  // This ensures data at 00:15:55 goes to 00:15:00 bucket, not 00:16:00
  return moment(key * 1000).startOf(interval).unix();
}

function roundKeyLegacyHour(key, idx, interval) {
  // Legacy behavior: for hour interval, round FIRST data point to nearest hour
  // Subsequent data points use startOf for consistency
  if (interval === 'hour' && idx === 0) {
    return Math.round(key / 3600) * 3600;
  }
  // For all other cases: round down to interval boundary
  return moment(key * 1000).startOf(interval).unix();
}

function buildRoundedMap(data, interval, roundingStrategy) {
  const rounded = {};
  Object.keys(data || {}).forEach((key, idx) => {
    const roundedKey = roundingStrategy === 'legacy-hour'
      ? roundKeyLegacyHour(parseInt(key, 10), idx, interval)
      : roundKeyByInterval(parseInt(key, 10), interval);
    rounded[roundedKey] = data[key];
  });

  return rounded;
}

export function buildFmsSeries({
  // deviceStatus = {},
  summary,
  // 'rollup' tanpa .data = deret waktunya emang belum ditarik. Default 'legacy'
  // biar pemanggil lama gak berubah perilaku.
  summarySource = 'legacy',
  dateRange,
  interval,
  roundingStrategy = 'interval',
  normalizeHourlyTimestamps = false,
  dataSort,
  intervalSecondsOverride,
}) {
  if (!summary) return [];

  if (summarySource === 'rollup' && !summary.gps?.data && !summary.rpm?.data
    && !summary.flowmeter?.data && !summary.ae?.data) {
    // Baris kosong sejumlah penuh kebaca kayak "gak ada data di periode ini".
    // Sengaja diem: deret waktu ditarik tanpa di-await, jadi render pertama
    // SELALU lewat sini. Halaman yang lupa withSeries ketauan dari chart kosong
    // permanen + nol /series/aggregated di Network, bukan dari console.
    return [];
  }

  const intervalSeconds = intervalSecondsOverride ?? getIntervalSeconds(interval);

  // Round start time down to the nearest interval boundary for proper alignment
  // This ensures generated timestamps match the rounded data keys
  const startMoment = moment(dateRange.start);
  const alignedStart = startMoment.startOf(interval).unix();
  // Samain sama end yang dipakai buat fetch (lihat asset-summary-loader.js): dilebarin ke akhir
  // menit yang dipilih, bukan mentah-mentah endUnix, biar bucket yang persis di boundary interval
  // (mis. end dipilih 00:00 pas Interval=Hour) tetep kegenerate jadi baris, bukan ke-skip strict `<`.
  const endUnix = moment(dateRange.end).endOf('minute').unix();

  let timestamps = generateTimestampsInSeconds(
    alignedStart,
    endUnix,
    intervalSeconds,
  );

  if (normalizeHourlyTimestamps) {
    timestamps = timestamps.map((timestamp, i) => {
      if (i === 0) return timestamp;
      return moment(timestamp * 1000).startOf('hour').unix();
    });
  }

  const roundedGps = buildRoundedMap(summary.gps?.data || {}, interval, roundingStrategy);
  const roundedRpm = buildRoundedMap(summary.rpm?.data || {}, interval, roundingStrategy);
  const roundedFm = buildRoundedMap(summary.flowmeter?.data || {}, interval, roundingStrategy);
  const roundedAe = buildRoundedMap(summary.ae?.data || {}, interval, roundingStrategy);
  const roundedTemperature = buildRoundedMap(summary.temperature?.data || {}, interval, roundingStrategy);
  const roundedPowermeter = buildRoundedMap(summary.powermeter?.data || {}, interval, roundingStrategy);

  let mappedSeries = timestamps.map((timestamp) => {
    const gpsData = roundedGps[timestamp] || {};
    const rpmData = roundedRpm[timestamp] || {
      STARBOARD: {},
      PORT: {},
      CENTER: {},
    };
    const fmData = roundedFm[timestamp] || {};
    const aeData = roundedAe[timestamp] || {};

    const entry = {
      timestamp,
      gpsData,
      rpmData,
      fmData,
      aeData,
    };

    entry.temperatureData = roundedTemperature?.[timestamp] || {
      PORT: {},
      STARBOARD: {},
      CENTER: {},
    };

    entry.powermeterData = roundedPowermeter?.[timestamp] || {};

    return entry;
  });

  // Filter out data that exceeds the user's selected end time
  // Backend uses < (exclusive) so we need to exclude data >= endUnix
  mappedSeries = mappedSeries.filter((entry) => entry.timestamp < endUnix);

  if (dataSort === 'latest') {
    mappedSeries = mappedSeries.sort((a, b) => b.timestamp - a.timestamp);
  }

  if (dataSort === 'oldest') {
    mappedSeries = mappedSeries.sort((a, b) => a.timestamp - b.timestamp);
  }

  return mappedSeries;
}

export function buildFmsSummary({
  deviceStatus = {},
  summary,
  summarySource = 'legacy',
  dateRange,
  interval,
  aeFuelFixed = false,
  seriesRoundingStrategy = 'interval',
}) {
  if (!summary) return null;

  const mappedSummary = {};
  const intervalSeconds = getIntervalSeconds(interval);
  const unixRangeStart = moment(dateRange.start).unix();
  const unixRangeEnd = moment(dateRange.end).unix();
  const intervalCount = Math.ceil((unixRangeEnd - unixRangeStart) / intervalSeconds);

  const lastGpsData = Object.values(summary.gps?.data || [{}])[Object.values(summary.gps?.data || [{}]).length - 1];
  // Endpoint rollup gak ngirim blok time-series, jadi lastGpsData jatuh ke {}
  // (truthy!) dan timestamp-nya undefined -> "Invalid date". Rollup nyediain
  // waktu terakhirnya di gps.summary.lastPosition.timestamp.
  const lastGpsTimestamp = lastGpsData?.timestamp
    || summary.gps?.summary?.lastPosition?.timestamp
    || null;
  const gpsCount = Object.values(summary.gps?.data || []).length;

  mappedSummary.gps = {
    lastGpsData,
    averageSpeed: formatNum(kmhToKnot(summary.gps?.summary?.averageSpeed), 2),
    totalDistance: formatNum(metersToNm(summary.gps?.summary?.totalDistance), 2),
    lastData: lastGpsTimestamp ? formatDateTime(lastGpsTimestamp * 1000, DISPLAY_DATETIME) : '-',
    percentage: formatNum(((gpsCount / intervalCount) * 100), 1),
    // Endpoint lama naruh zone di gps.summary.zone; rollup mindahin ke
    // gps.summary.lastPosition.zone (isinya identik, key-set sama).
    zone: summary.gps?.summary?.zone
      || summary.gps?.summary?.lastPosition?.zone
      || null,
  };

  const rpmCount = Object.values(summary.rpm?.data || []).length;
  const rpmTypes = deviceStatus?.rpmTypes || [];
  mappedSummary.rpm = {};

  rpmTypes.forEach((type) => {
    mappedSummary.rpm[type] = {
      lastData: Object.values(summary.rpm?.data || []).length > 0
        ? formatDateTime(
          Object.values(summary.rpm?.data)[Object.values(summary.rpm?.data).length - 1][type]?.timestamp * 1000,
          DISPLAY_DATETIME,
        )
        : '-',
      runningTime: toDuration(summary.rpm?.summary?.runningTime?.[type] || 0),
      average: formatNum((summary.rpm?.summary?.average?.[type] || 0), 2),
      lastRpmData: Object.values(summary.rpm?.data || []).length > 0
        ? Object.values(summary.rpm?.data)[Object.values(summary.rpm?.data).length - 1][type]
        : {},
    };
  });

  mappedSummary.rpm.percentage = formatNum(((rpmCount / intervalCount) * 100), 1);

  const fmSummary = summary.flowmeter?.summary;
  const fm = {};

  if (fmSummary) {
    // Iterasi per group label-driven (PORT/STBD/CENTER, AE4, AE_INLINE, ...).
    // Backend menyediakan objek per-label `${name}_IN/_OUT` dan turunan
    // `${name.toLowerCase()}EngineCons` untuk label apapun.
    const groups = deviceStatus?.flowmeterGroups || [];

    groups.forEach((group) => {
      const { name } = group;
      const inKey = `${name}_IN`;
      const outKey = `${name}_OUT`;
      const consKey = `${name.toLowerCase()}EngineCons`;
      const avgFlowKey = `${name.toLowerCase()}AverageFlow`;

      if (fmSummary[inKey] || fmSummary[consKey] || fmSummary[avgFlowKey]) {
        fm[name] = {
          in: {
            avgFuelFlow: formatNum((fmSummary[inKey]?._averageFlowrate || 0), 2),
            engineCons: formatNum((fmSummary[inKey]?._totalVolumeFlow || 0), 2),
            avgDensity: formatNum((fmSummary[inKey]?._averageDens || 0), 2),
          },
        };
      }

      if (fmSummary[outKey]) {
        if (!fm[name]) fm[name] = {};
        fm[name].out = {
          avgFuelFlow: formatNum((fmSummary[outKey]._averageFlowrate || 0), 2),
          engineCons: formatNum((fmSummary[outKey]._totalVolumeFlow || 0), 2),
          avgDensity: formatNum((fmSummary[outKey]._averageDens || 0), 2),
        };
      }
    });

    fm.meEngineCons = formatNum((fmSummary.meEngineCons || 0), 2);
  }

  const fmCount = Object.values(summary?.flowmeter?.data || []).length;
  fm.percentage = formatNum(((fmCount / intervalCount) * 100), 1);

  mappedSummary.fm = fm;

  const lastAeData = Object.values(summary?.ae?.data || []).length > 0
    ? Object.values(summary?.ae?.data)[Object.values(summary.ae?.data).length - 1]
    : null;
  const lastAeKey = Object.keys(summary?.ae?.data || []).length > 0
    ? Object.keys(summary?.ae?.data)[Object.keys(summary.ae?.data || [{}]).length - 1]
    : null;

  const aeCount = deviceStatus?.aeCount || 0;
  const formatAeFuel = (value) => (aeFuelFixed ? formatNum((value || 0), 2) : (value || '-'));

  const aeData = {};
  let totalRunningSeconds = 0;
  let totalFuelConsumption = 0;

  for (let i = 1; i <= aeCount; i += 1) {
    const aeKey = `AE${i}`;
    const aeNum = `ae${i}`;
    const fuelConsumption = summary.ae?.summary?.fuelConsumption?.[aeKey] || 0;
    const runningSeconds = summary.ae?.summary?.runningSeconds?.[aeKey] || 0;

    aeData[aeNum] = {
      aeNumber: i,
      fuelConsumption: formatAeFuel(fuelConsumption),
      runningSeconds: toDuration(runningSeconds),
      lastRunningTime: lastAeData?.[aeKey]?.runningTime || 0,
    };

    totalRunningSeconds += runningSeconds;
    totalFuelConsumption += fuelConsumption;
  }

  mappedSummary.ae = {
    ...aeData,
    lastAeData,
    lastData: lastAeKey ? formatDateTime(lastAeKey * 1000, DISPLAY_DATETIME) : '-',
    percentage: formatNum(((aeCount / intervalCount) * 100), 1),
    runningSeconds: toDuration(totalRunningSeconds),
    fuelConsumption: formatNum(totalFuelConsumption, 2),
  };

  const tempDetails = summary?.temperature?.summary?.tempDetails;
  const tempAvg = (group, position) => {
    const value = tempAverageC(tempDetails?.[group]?.[position]?.[0]);
    return value === null ? '-' : formatNum(value, 2);
  };

  mappedSummary.temperature = {
    exhaust: {
      port: { average: tempAvg('EXHAUST_TEMPERATURE', 'PORT') },
      starboard: { average: tempAvg('EXHAUST_TEMPERATURE', 'STARBOARD') },
      center: { average: tempAvg('EXHAUST_TEMPERATURE', 'CENTER') },
    },
    tank: {
      port: { average: tempAvg('TEMPERATURE', 'PORT') },
      starboard: { average: tempAvg('TEMPERATURE', 'STARBOARD') },
      center: { average: tempAvg('TEMPERATURE', 'CENTER') },
    },
  };

  mappedSummary.powermeter = [];
  if (summary?.powermeter?.summary?.devices) {
    const pmDevices = Object.keys(summary.powermeter.summary.devices);
    pmDevices.forEach((pmDeviceKey) => {
      const pmData = summary.powermeter.summary.devices[pmDeviceKey]?.[0] || {};
      mappedSummary.powermeter.push({
        name: pmData.devcName || pmDeviceKey.replace('PM_', '').replace('_', ' '),
        totalEnergy: formatNum((pmData.totalEnergy || 0), 2),
        avgActivePower: formatNum((pmData.avgActivePower || 0), 2),
        avgFrequency: formatNum((pmData.avgFrequency || 0), 2),
        avgVoltage: formatNum((pmData.avgVoltageLtoN || 0), 2),
        avgCurrent: formatNum((pmData.avgCurrent || 0), 2),
        avgPowerFactor: formatNum((pmData.avgPowerFactor || 0), 2),
      });
    });
  }

  mappedSummary.series = buildFmsSeries({
    summary,
    summarySource,
    dateRange,
    interval,
    roundingStrategy: seriesRoundingStrategy,
    deviceStatus,
  });

  return mappedSummary;
}
